import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';

// Action Types
const WIZARD_ACTIONS = {
  SET_CURRENT_STEP: 'SET_CURRENT_STEP',
  UPDATE_LANGUAGE_SELECTION: 'UPDATE_LANGUAGE_SELECTION',
  UPDATE_PERSONAL_INFO: 'UPDATE_PERSONAL_INFO',
  UPDATE_COUNTRY_SELECTION: 'UPDATE_COUNTRY_SELECTION',
  UPDATE_EDUCATION: 'UPDATE_EDUCATION',
  UPDATE_EXPERIENCE: 'UPDATE_EXPERIENCE',
  UPDATE_LANGUAGE_SKILLS: 'UPDATE_LANGUAGE_SKILLS',
  UPDATE_FINANCIAL_INFO: 'UPDATE_FINANCIAL_INFO',
  SET_CV_DATA: 'SET_CV_DATA',
  SET_SCORES: 'SET_SCORES',
  SET_RECOMMENDATIONS: 'SET_RECOMMENDATIONS',
  SET_REVIEW_DATA: 'SET_REVIEW_DATA',
  SET_LOADING: 'SET_LOADING',
  SET_ERRORS: 'SET_ERRORS',
  SET_FRAPPE_LEAD: 'SET_FRAPPE_LEAD',
  COMPLETE_WIZARD: 'COMPLETE_WIZARD',
  RESET_WIZARD: 'RESET_WIZARD',
  HYDRATE_STATE: 'HYDRATE_STATE' // For restoring from localStorage
};

// Initial State
const initialState = {
  currentStep: 1,
  isCompleted: false,
  languagePreference: {
    selectedLanguage: 'en'
  },
  personalInfo: {},
  countrySelection: {
    selectedCountry: null,
    dynamicQuestions: [],
    answers: {}
  },
  education: {},
  experience: [],
  languageSkills: {},
  financialInfo: {},
  cvData: null,
  scores: {},
  recommendations: [],
  reviewData: {}, // Store selected pathway and package from review step

  // Frappe lead tracking - created immediately after CV upload
  frappeLeadId: null,
  frappeLeadStatus: 'pending', // pending, created, failed, updated
  frappeLeadError: null,
  frappeCreatedAt: null,

  isLoading: false,
  errors: {}
};

// Reducer
function wizardReducer(state = initialState, action) {
  switch (action.type) {
    case WIZARD_ACTIONS.SET_CURRENT_STEP:
      return {
        ...state,
        currentStep: action.payload,
        errors: { ...state.errors, step: undefined }
      };

    case WIZARD_ACTIONS.UPDATE_LANGUAGE_SELECTION:
      return {
        ...state,
        languagePreference: { ...state.languagePreference, ...action.payload },
        errors: { ...state.errors, languagePreference: undefined }
      };

    case WIZARD_ACTIONS.UPDATE_COUNTRY_SELECTION:
      {
        const mergedAnswers = action.payload.answers
          ? { ...state.countrySelection.answers, ...action.payload.answers }
          : state.countrySelection.answers;

        const nextSelection = {
          ...state.countrySelection,
          ...action.payload,
          answers: mergedAnswers
        };

        return {
          ...state,
          countrySelection: nextSelection,
          errors: { ...state.errors, countrySelection: undefined }
        };
      }

    case WIZARD_ACTIONS.UPDATE_PERSONAL_INFO:
      return {
        ...state,
        personalInfo: { ...state.personalInfo, ...action.payload },
        errors: { ...state.errors, personalInfo: undefined }
      };

    case WIZARD_ACTIONS.UPDATE_EDUCATION:
      return {
        ...state,
        education: { ...state.education, ...action.payload },
        errors: { ...state.errors, education: undefined }
      };

    case WIZARD_ACTIONS.UPDATE_EXPERIENCE:
      return {
        ...state,
        experience: action.payload,
        errors: { ...state.errors, experience: undefined }
      };

    case WIZARD_ACTIONS.UPDATE_LANGUAGE_SKILLS:
      return {
        ...state,
        languageSkills: { ...state.languageSkills, ...action.payload },
        errors: { ...state.errors, languageSkills: undefined }
      };

    case WIZARD_ACTIONS.UPDATE_FINANCIAL_INFO:
      return {
        ...state,
        financialInfo: { ...state.financialInfo, ...action.payload },
        errors: { ...state.errors, financialInfo: undefined }
      };

    case WIZARD_ACTIONS.SET_REVIEW_DATA:
      return {
        ...state,
        reviewData: action.payload,
        errors: { ...state.errors, reviewData: undefined }
      };

    case WIZARD_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };

    case WIZARD_ACTIONS.SET_ERRORS:
      return {
        ...state,
        errors: { ...state.errors, ...action.payload }
      };

    case WIZARD_ACTIONS.SET_CV_DATA:
      return {
        ...state,
        cvData: action.payload
      };

    case WIZARD_ACTIONS.SET_FRAPPE_LEAD:
      return {
        ...state,
        frappeLeadId: action.payload.leadId,
        frappeLeadStatus: action.payload.status || 'created',
        frappeLeadError: action.payload.error || null,
        frappeCreatedAt: action.payload.createdAt || new Date().toISOString()
      };

    case WIZARD_ACTIONS.SET_SCORES:
      return {
        ...state,
        scores: action.payload,
        errors: { ...state.errors, scores: undefined }
      };

    case WIZARD_ACTIONS.SET_RECOMMENDATIONS:
      return {
        ...state,
        recommendations: action.payload,
        errors: { ...state.errors, recommendations: undefined }
      };

    case WIZARD_ACTIONS.COMPLETE_WIZARD:
      return {
        ...state,
        isCompleted: true,
        completedAt: new Date().toISOString()
      };

    case WIZARD_ACTIONS.RESET_WIZARD:
      return initialState;

    case WIZARD_ACTIONS.HYDRATE_STATE:
      // Restore state from localStorage, preserving any fields not in saved data
      return {
        ...initialState,
        currentStep: action.payload.currentStep || 1,
        languagePreference: {
          ...initialState.languagePreference,
          selectedLanguage: action.payload.selectedLanguage || 'en'
        },
        countrySelection: action.payload.countrySelection || initialState.countrySelection,
        personalInfo: action.payload.personalInfo || {},
        education: action.payload.education || {},
        experience: action.payload.experience || [],
        languageSkills: action.payload.languageSkills || {},
        cvData: action.payload.cvData || null,
        financialInfo: action.payload.financialInfo || {},
        reviewData: {
          selectedPathway: action.payload.selectedPathway || null,
          selectedPackage: action.payload.selectedPackage || null
        },
        // Restore Frappe lead tracking
        frappeLeadId: action.payload.frappeLeadId || null,
        frappeLeadStatus: action.payload.frappeLeadStatus || 'pending',
        frappeLeadError: action.payload.frappeLeadError || null,
      };

    default:
      return state;
  }
}

// Context
const WizardContext = createContext();

// Provider Component
export const WizardProvider = ({ children }) => {
  const [state, dispatch] = useReducer(wizardReducer, initialState);

  // Action creators wrapped in dispatch
  const actions = useMemo(() => ({
    setCurrentStep: (step) => dispatch({ type: WIZARD_ACTIONS.SET_CURRENT_STEP, payload: step }),
    updateLanguageSelection: (data) => dispatch({ type: WIZARD_ACTIONS.UPDATE_LANGUAGE_SELECTION, payload: data }),
    updateCountrySelection: (data) => dispatch({ type: WIZARD_ACTIONS.UPDATE_COUNTRY_SELECTION, payload: data }),
    updatePersonalInfo: (info) => dispatch({ type: WIZARD_ACTIONS.UPDATE_PERSONAL_INFO, payload: info }),
    updateEducation: (edu) => dispatch({ type: WIZARD_ACTIONS.UPDATE_EDUCATION, payload: edu }),
    updateExperience: (exp) => dispatch({ type: WIZARD_ACTIONS.UPDATE_EXPERIENCE, payload: exp }),
    updateLanguageSkills: (lang) => dispatch({ type: WIZARD_ACTIONS.UPDATE_LANGUAGE_SKILLS, payload: lang }),
    updateFinancialInfo: (fin) => dispatch({ type: WIZARD_ACTIONS.UPDATE_FINANCIAL_INFO, payload: fin }),
    setReviewData: (data) => dispatch({ type: WIZARD_ACTIONS.SET_REVIEW_DATA, payload: data }),
    setLoading: (isLoading) => dispatch({ type: WIZARD_ACTIONS.SET_LOADING, payload: isLoading }),
    setErrors: (errors) => dispatch({ type: WIZARD_ACTIONS.SET_ERRORS, payload: errors }),
    setCVData: (cvData) => dispatch({ type: WIZARD_ACTIONS.SET_CV_DATA, payload: cvData }),
    setFrappeLead: (leadData) => dispatch({ type: WIZARD_ACTIONS.SET_FRAPPE_LEAD, payload: leadData }),
    setScores: (scores) => dispatch({ type: WIZARD_ACTIONS.SET_SCORES, payload: scores }),
    setRecommendations: (recommendations) => dispatch({ type: WIZARD_ACTIONS.SET_RECOMMENDATIONS, payload: recommendations }),
    completeWizard: () => dispatch({ type: WIZARD_ACTIONS.COMPLETE_WIZARD }),
    resetWizard: () => dispatch({ type: WIZARD_ACTIONS.RESET_WIZARD }),
    hydrateState: (savedState) => dispatch({ type: WIZARD_ACTIONS.HYDRATE_STATE, payload: savedState })
  }), []);

  const value = {
    state,
    dispatch,
    actions
  };

  return (
    <WizardContext.Provider value={value}>
      {children}
    </WizardContext.Provider>
  );
};

// Hook to use wizard context
export const useWizard = () => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
};

export default WizardContext;
